import type { BillingProcess, LimitNames, PlanLimitsType, PlanTypes } from '@gonfalon/billing/types';

import { type planIdToPlan } from '../planData/planIdToPlan';

export type Invoice = {
  key: string;
  date: string;
  _links?: {
    doc?: {
      href?: string;
    };
  };
};

export type Plan = {
  id: keyof typeof planIdToPlan;
  planType: PlanTypes;
  billingProcess: BillingProcess;
  checkoutProcess: CheckoutProcess; // Next step in the checkout process
  marketingCopy: MarketingCopy; // Unique copy related to a specific plan
  versions: Record<string, Version>; // Plan versioning
  recommended?: boolean; // Whether a plan is emphasized in checkout
  disableIf?: Array<{ planType: PlanTypes; version?: number }>; // Disable button when subscription matches. Omit version to match any version of that plan.
  hasConfigurableLimits?: boolean; // Whether the plan has configurable limits (defaults to false for fixed plans)
  isFoundationPlan?: boolean; // Whether the plan is a Foundation-tier plan (shows payment card UI)
  isFreeDevPlan?: boolean; // Whether the plan is a free developer plan (hides payment card requirement)
  isDeveloperSignup?: boolean; // Whether this plan counts as a developer signup (for analytics tracking)
  isLegacyDeveloperFacade?: boolean; // Whether this is the legacy Developer plan facade (displays as "Developer / Foundation")
  useObservabilityFlagForVersion?: boolean; // Whether to check enableSelfServeObservability flag for version selection (defaults to false, meaning always use version 1)
};

export type BulkPricingTier = {
  min: number;
  max: number;
  pricePerBlockCents: number;
};

export type AnnualCommitOverageProductDetails = {
  hasBulkPricing?: boolean;
  isUnbilled?: boolean;
  limitName: LimitNames;
  freeQuantity: number;
  annualCommitDetails: {
    blockSize: number;
    pricePerBlockCents: number;
    maxQuantity: number;
    bulkPricingTiers?: BulkPricingTier[];
  };
  monthlyOverageDetails: {
    blockSize: number;
    pricePerBlockCents: number;
  };
  unitName?: string;
  displayOnly?: boolean; // Shown as inclusion but not billed (e.g. AI runs pre-billing)
  monthlyBilledOnly?: boolean; // Billed monthly but not annually; shows as disabled in annual tables (e.g. AI runs)
  gatedByFlag?: () => boolean; // See `getVisiblePlanProducts`
};

export type ProductVisibilityContext =
  | { source: 'session' }
  | { source: 'subscription'; subscriptionLimits: PlanLimitsType };

type AnnualCommitOverageVersion = {
  planTitle?: string;
  hasAnnualBilling?: boolean; // Whether this version supports annual billing (defaults to true)
  products: AnnualCommitOverageProductDetails[];
  marketingCopy?: Partial<MarketingCopy>; // Per-version marketing copy overrides merged on top of `Plan.marketingCopy`
  disableIf?: Array<{ planType: PlanTypes; version?: number }>; // Disable button when subscription matches. Omit version to match any version of that plan.
};

export type Version = AnnualCommitOverageVersion;
export type ProductDetails = AnnualCommitOverageProductDetails;

export type ResolvedVersion = Version & { versionKey: number | undefined };

export type MarketingCopy = {
  name: string; // e.g. 'Foundation'
  tagLine: string; // e.g. 'A feature-rich starter plan with essential tools for builders'
  pricingHeadline: string; // e.g. 'Monthly pay-as-you-go'
  pricingDescription: string; // e.g. 'Pay-as-you-go only when you exceed your monthly allowance'
  featuresHeader: string; // Header for feature list (e.g. 'Everything in Developer, plus')
  features: Feature[]; // Default feature list
  featureGroups?: FeatureGroup[]; // Grouped feature sections (e.g. CodeControl + AgentControl). Overrides features/featuresHeader when present.
  annualDiscount?: string; // e.g. '2 free months' - marketing copy for annual plan discount
  ctaButtonText?: string; // Custom CTA button text for plan selection (e.g. 'Select plan')
  checkoutCtaText?: string; // Custom CTA button text for checkout confirmation (e.g. 'Get started', 'Confirm subscription')
  monthlyPriceText?: string; // Custom monthly price display text (e.g. 'FREE', 'Usage-based')
  afterTrialTag?: AfterTrialTag; // Tag shown during trial to indicate this plan will be active after trial ends
};

export type AfterTrialTag = {
  text: string; // Tag text (e.g. "Plan after trial")
  tooltip: string; // Tooltip text explaining what happens after trial
};

export enum CheckoutProcess {
  DIRECT_CHECKOUT = 'directCheckout', // Fixed limits are provided for easy checkout
  CUSTOMIZE_COMMITS = 'customizeCommits', // Plan limits are customizable in checkout
  CONTACT_US = 'contactUs', // Changing a plan requires contacting us
}

export type Feature = {
  text: string;
  tooltip?: {
    text: string;
    ctaTarget?: string; // This should be a valid URL or a valid path in the app
  };
  gatedByFlag?: () => boolean; // Evaluated at render time; feature is hidden when this returns false.
};

export type FeatureGroup = {
  header: string; // Section header (e.g. 'CodeControl', 'AgentControl')
  features: Feature[];
};

export type CtaButtonProps = {
  plan: Plan;
  variant?: 'default' | 'primary';
  versionKey?: number;
};

export enum OverageInfoModalMode {
  MONTHLY = 'MONTHLY',
  AUTOMATIC = 'AUTOMATIC',
}
